import { getAvailableFonts } from '@remotion/google-fonts';

/**
 * Burned-in captions are mostly branding, so the typeface matters more here
 * than almost anything else in the app.
 */

export const SYSTEM_STACK =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, "Helvetica Neue", Arial, sans-serif';

export type CaptionFont = {
  id: string;
  label: string;
  /** null means the system stack — nothing to download. */
  importName: string | null;
  /**
   * Pinned per font rather than guessed. Requesting a weight a family does not
   * ship throws, and the display faces here only have 400 — they are heavy by
   * design. Pinning also avoids the unrestricted loadFont(), which pulls every
   * weight of every subset (~200 requests).
   */
  weight: string;
};

export const CAPTION_FONTS: CaptionFont[] = [
  { id: 'system', label: 'System (no download)', importName: null, weight: '800' },
  { id: 'inter', label: 'Inter', importName: 'Inter', weight: '800' },
  { id: 'montserrat', label: 'Montserrat', importName: 'Montserrat', weight: '800' },
  { id: 'poppins', label: 'Poppins', importName: 'Poppins', weight: '800' },
  { id: 'oswald', label: 'Oswald (condensed)', importName: 'Oswald', weight: '700' },
  { id: 'anton', label: 'Anton (display)', importName: 'Anton', weight: '400' },
  { id: 'bebas', label: 'Bebas Neue (display)', importName: 'BebasNeue', weight: '400' },
  { id: 'archivo', label: 'Archivo Black (display)', importName: 'ArchivoBlack', weight: '400' },
];

const cache = new Map<string, string>();

/** Resolves to the CSS font-family to render with. */
export const loadCaptionFont = async (font: CaptionFont): Promise<string> => {
  if (!font.importName) return SYSTEM_STACK;
  const cached = cache.get(font.id);
  if (cached) return cached;

  const entry = getAvailableFonts().find((f) => f.importName === font.importName);
  if (!entry) throw new Error(`Font "${font.label}" is not available.`);

  const mod = await entry.load();
  const { fontFamily } = mod.loadFont('normal', {
    weights: [font.weight],
    subsets: ['latin'],
  });
  cache.set(font.id, fontFamily);
  return fontFamily;
};

/**
 * A brand font usually arrives as a file, not a Google entry. Registering it
 * through FontFace keeps it same-origin — no network request, so none of the
 * cross-origin isolation rules apply to it at all.
 */
export const loadCustomFont = async (file: File): Promise<string> => {
  const family = `Custom ${file.name.replace(/\.[^.]+$/, '')}`;
  const face = new FontFace(family, await file.arrayBuffer());
  await face.load();
  document.fonts.add(face);
  return family;
};
